import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { contactDetailsRequireReclassification } from "tng-address-collection-shared/contactDetailsRequireReclassification.ts";
import {
  CardsListItem,
  EditCardRequest,
  ListCardsResponseWithProjectName,
} from "tng-address-collection-shared/types/apiTypes.ts";
import {
  getSenderDisplayName,
  getSenderSignature,
  optimisticSignatureFields,
  withSenderSignature,
} from "./getSenderSignature.ts";
import { queryKeys } from "./queryKeys.ts";

export const useEditCardProjectCache = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (newCard: EditCardRequest, userUidNumber?: number) => {
      queryClient.setQueryData<ListCardsResponseWithProjectName>(
        queryKeys.cardsInProject(newCard.projectId),
        (old) => {
          const oldCard = old?.items.find((card) => card.id === newCard.id);
          const shouldResetWarning = contactDetailsRequireReclassification(
            oldCard && {
              firstName: oldCard.firstName,
              lastName: oldCard.lastName,
              email: oldCard.email,
              salutation: oldCard.salutation,
            },
            {
              firstName: newCard.recipient.firstName,
              lastName: newCard.recipient.lastName,
              email: newCard.recipient.email,
              salutation: newCard.recipient.salutationKind,
            },
          );
          const senderSignature = getSenderSignature(
            queryClient,
            newCard.senderId,
          );
          const signatureFields = optimisticSignatureFields(
            withSenderSignature(oldCard?.signatures ?? [], senderSignature),
            old?.hiddenFields,
            userUidNumber,
          );
          const senderDisplayName = getSenderDisplayName(
            queryClient,
            newCard.senderId,
          );
          const tempCard: CardsListItem = {
            ...oldCard,
            id: newCard.id,
            projectId: newCard.projectId,
            responsibleProjectId:
              oldCard?.responsibleProjectId ?? newCard.projectId,
            shared: oldCard?.shared ?? false,
            firstName: newCard.recipient.firstName,
            lastName: newCard.recipient.lastName,
            companyName: newCard.recipient.companyName,
            departmentName: newCard.departmentName,
            senderId: newCard.senderId,
            senderDisplayName,
            manualDispatch: newCard.manualDispatch,
            email: newCard.recipient.email,
            comment: newCard.comment,
            hasContactDetailsWarning: shouldResetWarning
              ? false
              : (oldCard?.hasContactDetailsWarning ?? false),
            senderUnknown: false,
            signatures: signatureFields.signatures,
            numberOfSignatures: signatureFields.numberOfSignatures,
            isSignedByUser:
              (oldCard?.isSignedByUser ?? false) ||
              signatureFields.isSignedByUser,
            language: newCard.recipient.preferredLanguage,
            title: newCard.recipient.title,
            salutation: newCard.recipient.salutationKind,
            receivesGift: newCard.receivesGift,
            formality: newCard.formality,
          };

          const updatedResponse: ListCardsResponseWithProjectName = {
            projectDisplayName: old?.projectDisplayName || "",
            items: [
              ...(old?.items.filter((card) => card.id !== newCard.id) || []),
              tempCard,
            ],
            hiddenFields: old?.hiddenFields || [],
            projectShepherds: old?.projectShepherds || {
              groups: [],
              individuals: [],
            },
            projectInfoBox: old?.projectInfoBox,
            hasPermissions: old?.hasPermissions ?? true,
          };
          return updatedResponse;
        },
      );
    },
    [queryClient],
  );
};
